/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { useState, useEffect } from 'react';
import { Collapse, Form, FormGroup, Row, Col, Button, InputGroup, Image } from 'react-bootstrap';
import { FairActions } from './FairActions';
import { useDispatch } from 'react-redux';
import './FairList.scss';
import { GPS } from '@umati/dashboard-types';
import { Fair } from 'Api/generated';
import { URLS } from 'Api/Api';

interface FairProps {
  fair: Fair;
  isAdd: boolean;
  existingFairTags: Fair['Tag'][];
}

const Fairs = ({ fair, isAdd, existingFairTags }: FairProps) => {
  const existingFairTagsSet = new Set(existingFairTags);
  const [state, setState] = useState({ ...fair });
  const [open, setOpen] = useState(false);
  const [newHall, setNewHall] = useState('new');
  const dispatch = useDispatch();

  const logoUpload: React.RefObject<HTMLInputElement | null> = React.createRef();
  const logoUploadId = `${state.Id}-logoUpload`;

  const mapUpload: React.RefObject<HTMLInputElement | null> = React.createRef();
  const mapUploadId = `${state.Id}-iconUpload`;

  useEffect(() => {
    setState(fair);
  }, [fair]);

  const getKeys = Object.keys as <T extends object>(obj: T) => Array<keyof T>;

  const imgUploadChange = () => {
    if (!logoUpload.current || !logoUpload.current.files || !logoUpload.current.files[0]) {
      return;
    }
    const file = logoUpload.current.files[0];
    if (file) {
      FairActions.UploadLogo(fair, file)(dispatch);
    }
  };

  const mapUploadChange = () => {
    if (!mapUpload.current || !mapUpload.current.files || !mapUpload.current.files[0]) {
      return;
    }
    const file = mapUpload.current.files[0];
    if (file) {
      FairActions.UploadMap(fair, file)(dispatch);
    }
  };

  function onInput<K extends keyof Fair>(key: K, value: Fair[K]) {
    const s = { ...state, [key]: value };
    setState(s);
  }

  const deleteFair = () => {
    FairActions.DeleteFair(state)(dispatch);
  };

  const saveFair = () => {
    FairActions.UpdateFair(state)(dispatch);
  };

  const addFair = () => {
    FairActions.CreateFair(state)(dispatch);
    setState(fair);
  };

  const isReadOnly = (field: string) => {
    const readOnlyFields = ['Id'];
    return readOnlyFields.includes(field);
  };

  const addHall = (hall: string) => {
    const halls = state.Halls ? state.Halls.slice() : [];
    halls.push(hall);
    setState({ ...state, Halls: halls });
  };

  const removeHall = (idx: number) => {
    const halls = state.Halls ? [...state.Halls] : [];
    if (halls) {
      if (idx === 0) {
        halls.shift();
      } else {
        halls.splice(idx, 1);
      }
      setState({ ...state, Halls: halls });
    }
  };

  const setPosition = (value: number, key: keyof GPS) => {
    const position = state.Position ? { ...state.Position } : { lat: 0, lng: 0 };
    position[key] = value;
    setState({ ...state, Position: position });
  };

  return (
    <li className="list-group-item" id={state.Tag || undefined}>
      <b>
        {`${state.Tag}|${state.Id}`}{' '}
        <Button
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          variant="Light"
          aria-expanded={open}
        >
          Expand
        </Button>
      </b>
      {isAdd === false ? (
        <>
          <Button className="btn-inline right" onClick={() => saveFair()}>
            Save
          </Button>
          <Button className="btn-inline right" onClick={() => deleteFair()} variant="danger">
            Delete
          </Button>
        </>
      ) : (
        <Button className="btn-inline right" disabled={existingFairTagsSet.has(state.Tag)} onClick={() => addFair()}>
          Add
        </Button>
      )}
      <Collapse in={open}>
        <div>
          <Form>
            {getKeys(state).map((key: keyof Fair) => {
              const val = state[key];
              return (
                ((typeof val === 'string' || typeof val === 'number' || typeof val === 'undefined') && (
                  <FormGroup as={Row} key={key}>
                    <Form.Label column sm="2">
                      {key}
                    </Form.Label>
                    <Col sm="10">
                      <Form.Control
                        as="input"
                        value={val}
                        id={key}
                        onChange={(e) => onInput(key, e.target.value)}
                        readOnly={isReadOnly(key)}
                      />
                    </Col>
                  </FormGroup>
                )) ||
                (typeof val === 'boolean' && (
                  <FormGroup as={Row} key={key}>
                    <Form.Label column sm="2">
                      {key}
                    </Form.Label>
                    <Col sm="10">
                      <Form.Check
                        type="checkbox"
                        checked={val}
                        id={key}
                        onChange={(e) => onInput(key, e.target.checked)}
                        readOnly={isReadOnly(key)}
                      />
                    </Col>
                  </FormGroup>
                ))
              );
            })}
          </Form>
          <Form>
            {state.Position && (
              <Form.Group as={Row}>
                <Form.Label column sm="2">
                  Position
                </Form.Label>
                <Col>
                  <Form.Group as={Row}>
                    <Form.Label column sm="4">
                      lat
                    </Form.Label>
                    <Col sm="8">
                      <Form.Control
                        type="number"
                        value={state.Position?.lat}
                        onChange={(e) => setPosition(parseFloat(e.target.value), 'lat')}
                      ></Form.Control>
                    </Col>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group as={Row}>
                    <Form.Label column sm="4">
                      lng
                    </Form.Label>
                    <Col sm="8">
                      <Form.Control
                        type="number"
                        value={state.Position?.lng}
                        onChange={(e) => setPosition(parseFloat(e.target.value), 'lng')}
                      ></Form.Control>
                    </Col>
                  </Form.Group>
                </Col>
              </Form.Group>
            )}
          </Form>
          <Form>
            <Form.Group as={Row}>
              <Form.Label column sm="2">
                Halls
              </Form.Label>
              <Col sm="10">
                <div className="btn-group column">
                  {state.Halls?.map((hall, idx) => {
                    return (
                      <Button variant="outline-secondary" key={hall} onClick={() => removeHall(idx)}>
                        {hall}
                      </Button>
                    );
                  })}
                  <InputGroup className="sm-3">
                    <Form.Control value={newHall} onChange={(e) => setNewHall(e.target.value)}></Form.Control>
                    <Button
                      disabled={state.Halls?.includes(newHall)}
                      onClick={() => addHall(newHall)}
                      variant="outline-secondary"
                    >
                      Add
                    </Button>
                  </InputGroup>
                </div>
              </Col>
            </Form.Group>
          </Form>
          <Row>
            <Col>
              <Image className="fair-logo" src={URLS.Fairs.getLogo(state.ImageFile || '')} fluid />
            </Col>
            <Col>
              <Image className="fair-logo" src={URLS.Fairs.getMap(state.Tag || '')} fluid />
            </Col>
          </Row>
          <Row>
            <Col>
              <input
                ref={logoUpload}
                type="file"
                id={logoUploadId}
                onChange={() => imgUploadChange()}
                accept=".jpg,.jpeg,.png"
                hidden
              />
              <label className="btn btn-primary btn-inline" htmlFor={logoUploadId}>
                Logo Upload
              </label>
            </Col>
            <Col>
              <input
                ref={mapUpload}
                type="file"
                id={mapUploadId}
                onChange={() => mapUploadChange()}
                accept=".svg"
                hidden
              />
              <label className="btn btn-primary btn-inline" htmlFor={mapUploadId}>
                Map Upload
              </label>
            </Col>
          </Row>
        </div>
      </Collapse>
    </li>
  );
};

export default Fairs;
